import React, { useState, useCallback, useMemo, useEffect } from 'react';
import {
  Box,
  Paper,
  TextField,
  Typography,
  Link,
  CircularProgress,
  Alert,
  useTheme,
  Fade,
  InputAdornment,
  IconButton,
} from '@mui/material';
import { Visibility, VisibilityOff } from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../hooks/useReduxAuth';
import { useFormValidation, validationSchemas } from '../../utils/validation';
import { MyButton, ForgotPasswordModal } from '../../components';
import { loginPageStyles } from './LoginPage.styles';

const LoginPage = () => {
  const theme = useTheme();
  const { t } = useTranslation();
  const navigate = useNavigate();

  const {
    login,
    isLoading,
    error: authError,
    clearError,
    isAuthenticated,
    isInitialized,
  } = useAuth();

  const [showPassword, setShowPassword] = useState(false);
  const [forgotPasswordModalOpen, setForgotPasswordModalOpen] = useState(false);

  const {
    data: formData,
    errors,
    touched,
    handleChange,
    handleBlur,
    validateAll,
    reset,
    isValid,
  } = useFormValidation({ email: '', password: '' }, validationSchemas.login);

  const handleInputFocus = useCallback(() => {
    if (authError) clearError();
  }, [authError, clearError]);

  const handleInputChange = useCallback(
    field => event => handleChange(field, event.target.value),
    [handleChange],
  );

  const handleTogglePasswordVisibility = useCallback(
    () => setShowPassword(prev => !prev),
    [],
  );

  const handleSubmit = useCallback(
    async event => {
      event.preventDefault();
      const { isValid: formIsValid } = validateAll();
      if (!formIsValid) return;
      try {
        const result = await login(formData.email, formData.password);
        if (result.success) {
          reset();
          if (authError) clearError();
          navigate('/', { replace: true });
        }
      } catch (error) {}
    },
    [formData.email, formData.password, validateAll, login, reset, authError, clearError, navigate],
  );

  const isSubmitDisabled = useMemo(
    () => isLoading || !formData.email || !formData.password || !isValid,
    [isLoading, formData.email, formData.password, isValid],
  );

  const passwordEndAdornment = useMemo(
    () => (
      <InputAdornment position="end">
        <IconButton
          onClick={handleTogglePasswordVisibility}
          onMouseDown={e => e.preventDefault()}
          edge="end"
          disabled={isLoading}
          size="small"
        >
          {showPassword ? <VisibilityOff fontSize="small" /> : <Visibility fontSize="small" />}
        </IconButton>
      </InputAdornment>
    ),
    [showPassword, handleTogglePasswordVisibility, isLoading],
  );

  useEffect(() => {
    if (isAuthenticated && isInitialized) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, isInitialized, navigate]);

  return (
    <Box sx={loginPageStyles.container(theme)}>
      {/* Arka plan dekor bloblari (yumusak radial glow) */}
      <Box sx={loginPageStyles.backgroundDecor(theme)} />
      <Box sx={loginPageStyles.backgroundDecorExtra(theme)} />

      <Fade in timeout={400}>
        <Paper sx={loginPageStyles.loginCard(theme)}>
          {/* Logo */}
          <Box sx={loginPageStyles.logoBox}>
            <img
              src="/istanbulproje-icon.png"
              alt="İstanbul Proje"
              style={loginPageStyles.logoImg}
            />
          </Box>

          {/* Başlık */}
          <Box sx={loginPageStyles.header}>
            <Typography variant="h5" sx={loginPageStyles.title}>
              {t('auth.login.title')}
            </Typography>
            <Typography variant="body2" sx={loginPageStyles.subtitle}>
              {t('auth.login.subtitle')}
            </Typography>
            {!isInitialized && (
              <Box sx={{ display: 'flex', justifyContent: 'center', mt: 1 }}>
                <CircularProgress size={14} />
              </Box>
            )}
          </Box>

          {/* Hata */}
          {authError && (
            <Fade in>
              <Alert
                severity="error"
                sx={loginPageStyles.errorAlert}
                onClose={clearError}
              >
                {authError}
              </Alert>
            </Fade>
          )}

          {/* Form */}
          <Box
            component="form"
            onSubmit={handleSubmit}
            sx={loginPageStyles.form}
            noValidate
            autoComplete="on"
          >
            <TextField
              fullWidth
              size="medium"
              label={t('auth.login.email')}
              type="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange('email')}
              onFocus={handleInputFocus}
              onBlur={() => handleBlur('email')}
              error={touched.email && !!errors.email}
              helperText={touched.email && errors.email}
              disabled={isLoading}
              autoComplete="email"
              autoCapitalize="none"
              spellCheck={false}
              required
              sx={loginPageStyles.textField}
            />

            <TextField
              fullWidth
              size="medium"
              label={t('auth.login.password')}
              type={showPassword ? 'text' : 'password'}
              name="password"
              value={formData.password}
              onChange={handleInputChange('password')}
              onFocus={handleInputFocus}
              onBlur={() => handleBlur('password')}
              error={touched.password && !!errors.password}
              helperText={touched.password && errors.password}
              disabled={isLoading}
              autoComplete="current-password"
              required
              sx={loginPageStyles.textField}
              InputProps={{ endAdornment: passwordEndAdornment }}
            />

            <Box sx={{ display: 'flex', justifyContent: 'flex-end', mt: -0.75 }}>
              <Link
                component="button"
                type="button"
                onClick={() => setForgotPasswordModalOpen(true)}
                disabled={isLoading}
                sx={loginPageStyles.forgotPassword}
              >
                {t('auth.login.forgotPassword', 'Şifremi Unuttum')}
              </Link>
            </Box>

            <MyButton
              type="submit"
              variant="contained"
              color="primary"
              fullWidth
              disabled={isSubmitDisabled}
              sx={loginPageStyles.loginButton(theme)}
              size="large"
            >
              {isLoading ? (
                <>
                  <CircularProgress size={18} sx={{ mr: 1 }} color="inherit" />
                  {t('auth.login.authenticating', 'Giriş yapılıyor...')}
                </>
              ) : (
                t('auth.login.loginButton')
              )}
            </MyButton>
          </Box>

          <Typography sx={loginPageStyles.footer}>
            © {new Date().getFullYear()} İstanbul Proje
          </Typography>
        </Paper>
      </Fade>

      <ForgotPasswordModal
        open={forgotPasswordModalOpen}
        onClose={() => setForgotPasswordModalOpen(false)}
      />
    </Box>
  );
};

export default LoginPage;
